/* ============================================================
 * AYU Funnel Page – Analog zu MENTO Funnel
 * Design: Gold + Olive auf warmem Ivory/Dark
 * Protagonist: Annam Hanfstein
 * ============================================================ */
import { useState, useEffect, useRef, useCallback } from "react";
import { IMAGES, TESTIMONIALS } from "@/lib/constants";
import { useI18n } from "@/contexts/I18nContext";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import {
  Leaf, Heart, Sun, Moon, Shield, Brain,
  ChevronDown, Play, Check, ArrowRight,
  Flame, Wind, Droplets, Zap,
  Mail, Phone, MessageCircle
} from "lucide-react";
import { motion } from "framer-motion";

/* ─── Scroll Animation Hook ─── */
function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ─── Funnel Navigation ─── */
function FunnelNav() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? "bg-[oklch(0.97_0.005_80/0.95)] backdrop-blur-md shadow-sm py-3" : "bg-transparent py-5"
    }`}>
      <div className="container flex items-center justify-between">
        <a href="/" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="AYU" className="h-10 w-auto" />
        </a>
        <a
          href="#start"
          className="px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 hover:scale-105"
          style={{
            background: "oklch(0.52 0.09 128)",
            color: "white",
          }}
        >
          {t("funnel.navCta")}
        </a>
      </div>
    </nav>
  );
}

/* ─── 1. Hero Section ─── */
function FunnelHero() {
  const { t } = useI18n();
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={IMAGES.nature} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, oklch(0.97 0.005 80 / 0.85) 0%, oklch(0.95 0.01 80 / 0.7) 50%, oklch(0.97 0.005 80 / 0.85) 100%)" }} />
      </div>

      <div className="container relative z-10 pt-28 pb-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-[oklch(0.52_0.09_128/0.3)] bg-[oklch(0.52_0.09_128/0.05)]">
            <div className="w-2 h-2 rounded-full bg-[oklch(0.52_0.09_128)] animate-pulse" />
            <span className="text-sm font-medium text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.heroBadge")}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] text-[oklch(0.2_0.02_80)] mb-6">
            {t("funnel.heroTitle1")}{" "}
            <br />
            <span className="italic text-[oklch(0.52_0.09_128)]">{t("funnel.heroTitle2")}</span>
          </h1>

          <p className="text-lg text-[oklch(0.4_0.02_80)] mb-10 max-w-lg leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.heroDesc")}
          </p>

          <div className="flex flex-wrap gap-4 mb-10">
            <a
              href="#start"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg"
              style={{ background: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}
            >
              {t("funnel.heroCtaPrimary")} <ArrowRight size={18} />
            </a>
            <a
              href="#was-ist-ayu"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:bg-[oklch(0.52_0.09_128/0.05)] text-[oklch(0.3_0.02_80)] border border-[oklch(0.3_0.02_80/0.2)]"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("funnel.heroCtaSecondary")} <ChevronDown size={18} />
            </a>
          </div>

          <div className="flex flex-wrap gap-6">
            {[
              { icon: Shield, label: t("funnel.heroBadgeDsgvo") },
              { icon: Leaf, label: t("funnel.heroBadgeNatural") },
              { icon: Sun, label: t("funnel.heroBadgePersonalized") },
            ].map((badge, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-sm border border-[oklch(0.52_0.09_128/0.15)]">
                <badge.icon size={14} className="text-[oklch(0.52_0.09_128)]" />
                <span className="text-xs font-medium text-[oklch(0.35_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={24} className="text-[oklch(0.52_0.09_128/0.5)]" />
      </div>
    </section>
  );
}

/* ─── 2. Pain Points ─── */
function PainPointsSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();
  const painPoints = [
    { icon: Flame, title: t("funnel.pain1Title"), text: t("funnel.pain1Text") },
    { icon: Wind, title: t("funnel.pain2Title"), text: t("funnel.pain2Text") },
    { icon: Droplets, title: t("funnel.pain3Title"), text: t("funnel.pain3Text") },
    { icon: Zap, title: t("funnel.pain4Title"), text: t("funnel.pain4Text") },
  ];

  return (
    <section className="py-24 lg:py-32 bg-[oklch(0.97_0.005_80)]" ref={ref}>
      <div className="container">
        <div className="mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.painBadge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] mt-4">
            {t("funnel.painTitle1")} <em className="text-[oklch(0.52_0.09_128)]">{t("funnel.painTitle2")}</em> {t("funnel.painTitle3")}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {painPoints.map((pp, i) => (
            <div
              key={i}
              className={`p-8 rounded-2xl bg-white border border-[oklch(0.9_0.01_80)] transition-all duration-500 hover:shadow-lg hover:border-[oklch(0.52_0.09_128/0.2)] ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-[oklch(0.52_0.09_128/0.08)] flex items-center justify-center mb-5">
                <pp.icon size={22} className="text-[oklch(0.52_0.09_128)]" />
              </div>
              <h3 className="text-lg font-bold text-[oklch(0.2_0.02_80)] mb-3">{pp.title}</h3>
              <p className="text-[oklch(0.45_0.02_80)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>{pp.text}</p>
            </div>
          ))}
        </div>

        <p className="text-center mt-12 text-[oklch(0.52_0.09_128)] italic max-w-xl mx-auto" style={{ fontFamily: "var(--font-body)" }}>
          {t("funnel.painOutro")}
        </p>
      </div>
    </section>
  );
}

/* ─── Inline CTA Banner ─── */
function InlineCta({ text, buttonText, variant = "light" }: { text: string; buttonText: string; variant?: "light" | "dark" | "accent" }) {
  const { ref, visible } = useInView();
  const isDark = variant === "dark";
  const isAccent = variant === "accent";

  return (
    <section
      ref={ref}
      className={`py-14 lg:py-16 transition-all duration-700 ${visible ? "opacity-100" : "opacity-0"}`}
      style={{
        background: isDark
          ? "oklch(0.18 0.02 280)"
          : isAccent
          ? "linear-gradient(135deg, oklch(0.52 0.09 128 / 0.08), oklch(0.78 0.11 85 / 0.05))"
          : "oklch(0.97 0.005 80)",
      }}
    >
      <div className="container">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
          <p
            className={`text-lg sm:text-xl font-semibold text-center sm:text-left ${
              isDark ? "text-[oklch(0.9_0.01_80)]" : "text-[oklch(0.25_0.02_80)]"
            }`}
          >
            {text}
          </p>
          <a
            href="#start"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg shrink-0"
            style={{ background: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}
          >
            {buttonText} <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── 3. So fühlt sich AYU an ─── */
function FeelSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();
  const features = [
    { icon: Heart, title: t("funnel.feel1Title"), text: t("funnel.feel1Text") },
    { icon: Brain, title: t("funnel.feel2Title"), text: t("funnel.feel2Text") },
    { icon: Sun, title: t("funnel.feel3Title"), text: t("funnel.feel3Text") },
  ];

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <img src={IMAGES.mandala} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="container relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.feelBadge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] mt-4 mb-6">
            {t("funnel.feelTitle1")}{" "}
            <br className="hidden sm:block" />
            <span className="text-[oklch(0.52_0.09_128)]">{t("funnel.feelTitle2")}</span>
          </h2>
          <p className="text-lg text-[oklch(0.45_0.02_80)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.feelDesc")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div
              key={i}
              className={`text-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-[oklch(0.9_0.01_80)] transition-all duration-500 hover:shadow-lg ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="w-14 h-14 rounded-2xl bg-[oklch(0.52_0.09_128/0.1)] flex items-center justify-center mx-auto mb-5">
                <f.icon size={24} className="text-[oklch(0.52_0.09_128)]" />
              </div>
              <h3 className="text-lg font-bold text-[oklch(0.2_0.02_80)] mb-3">{f.title}</h3>
              <p className="text-[oklch(0.45_0.02_80)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 4. Was ist AYU? – 3-Schritte ─── */
function WhatIsAyuSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();
  const steps = [
    { num: t("funnel.step1Num"), title: t("funnel.step1Title"), text: t("funnel.step1Text") },
    { num: t("funnel.step2Num"), title: t("funnel.step2Title"), text: t("funnel.step2Text") },
    { num: t("funnel.step3Num"), title: t("funnel.step3Title"), text: t("funnel.step3Text") },
  ];

  return (
    <section id="was-ist-ayu" className="py-24 lg:py-32 bg-[oklch(0.97_0.005_80)]" ref={ref}>
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.whatBadge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] mt-4 mb-6">
            {t("funnel.whatTitle1")}{" "}
            <br className="hidden sm:block" />
            {t("funnel.whatTitle2")}
          </h2>
          <p className="text-lg text-[oklch(0.45_0.02_80)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.whatDesc")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {steps.map((s, i) => (
            <div
              key={i}
              className={`relative text-center transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 200}ms` }}
            >
              <div className="text-5xl font-bold text-[oklch(0.52_0.09_128/0.15)] mb-4" style={{ fontFamily: "var(--font-display)" }}>{s.num}</div>
              <h3 className="text-xl font-bold text-[oklch(0.2_0.02_80)] mb-3">{s.title}</h3>
              <p className="text-[oklch(0.45_0.02_80)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>{s.text}</p>
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 -right-4 w-8 text-[oklch(0.52_0.09_128/0.2)]">
                  <ArrowRight size={24} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 5. DNA-Test / Körper-Verbindung ─── */
function DnaSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" style={{ background: "oklch(0.18 0.02 280)" }} ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <img src={IMAGES.dna} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.78_0.11_85)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.dnaBadge")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.95_0.01_80)] mt-4 mb-6">
              {t("funnel.dnaTitle1")}{" "}
              <br className="hidden sm:block" />
              {t("funnel.dnaTitle2")}
            </h2>
            <p className="text-lg text-[oklch(0.75_0.01_80)] leading-relaxed mb-8" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.dnaDesc")}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                t("funnel.dnaFeature1"),
                t("funnel.dnaFeature2"),
                t("funnel.dnaFeature3"),
                t("funnel.dnaFeature4"),
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Check size={16} className="text-[oklch(0.52_0.09_128)] shrink-0" />
                  <span className="text-sm text-[oklch(0.75_0.01_80)]" style={{ fontFamily: "var(--font-body)" }}>{item}</span>
                </div>
              ))}
            </div>

            <p className="text-sm text-[oklch(0.6_0.01_80)] italic" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.dnaDisclaimer")}
            </p>
          </div>

          <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl" style={{ background: "linear-gradient(135deg, oklch(0.52 0.09 128 / 0.2), oklch(0.78 0.11 85 / 0.1))", filter: "blur(40px)" }} />
              <div className="relative bg-[oklch(0.22_0.02_280)] rounded-3xl p-10 border border-[oklch(0.78_0.11_85/0.15)]">
                <div className="text-center mb-8">
                  <div className="text-5xl font-bold text-[oklch(0.78_0.11_85)]" style={{ fontFamily: "var(--font-display)" }}>1.900+</div>
                  <div className="text-sm text-[oklch(0.6_0.01_80)] mt-2" style={{ fontFamily: "var(--font-body)" }}>{t("funnel.dnaStatLabel")}</div>
                </div>
                <div className="space-y-4">
                  {[
                    t("funnel.dnaBenefit1"),
                    t("funnel.dnaBenefit2"),
                    t("funnel.dnaBenefit3"),
                    t("funnel.dnaBenefit4"),
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-[oklch(0.52_0.09_128)] mt-0.5 shrink-0" />
                      <span className="text-sm text-[oklch(0.75_0.01_80)]" style={{ fontFamily: "var(--font-body)" }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 6. Zwei Wege ─── */
function TwoPathsSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();

  return (
    <section className="py-24 lg:py-32 bg-[oklch(0.97_0.005_80)]" ref={ref}>
      <div className="container">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.pathsBadge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] mt-4">
            {t("funnel.pathsTitle")}
          </h2>
          <p className="text-lg text-[oklch(0.45_0.02_80)] mt-4" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.pathsDesc")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Path 1 */}
          <div className={`p-8 rounded-3xl bg-white border border-[oklch(0.9_0.01_80)] transition-all duration-500 hover:shadow-xl ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}>
            <h3 className="text-2xl font-bold text-[oklch(0.2_0.02_80)] mb-2">{t("funnel.path1Title")}</h3>
            <p className="text-[oklch(0.52_0.09_128)] font-medium mb-6" style={{ fontFamily: "var(--font-body)" }}>{t("funnel.path1Sub")}</p>
            <p className="text-[oklch(0.45_0.02_80)] mb-6 leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.path1Desc")}
            </p>
            <ul className="space-y-3 mb-8">
              {[
                t("funnel.path1Feature1"),
                t("funnel.path1Feature2"),
                t("funnel.path1Feature3"),
                t("funnel.path1Feature4"),
                t("funnel.path1Feature5"),
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check size={16} className="text-[oklch(0.52_0.09_128)] shrink-0" />
                  <span className="text-sm text-[oklch(0.35_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>{item}</span>
                </li>
              ))}
            </ul>
            <a href="#start" className="inline-flex items-center gap-2 text-[oklch(0.52_0.09_128)] font-semibold hover:gap-3 transition-all" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.path1Cta")} <ArrowRight size={16} />
            </a>
          </div>

          {/* Path 2 */}
          <div className={`p-8 rounded-3xl border-2 border-[oklch(0.52_0.09_128/0.3)] relative transition-all duration-500 hover:shadow-xl ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`} style={{ transitionDelay: "150ms", background: "linear-gradient(135deg, oklch(0.52 0.09 128 / 0.03), oklch(0.78 0.11 85 / 0.03))" }}>
            <div className="absolute -top-3 right-6 px-4 py-1 rounded-full text-xs font-bold text-white" style={{ background: "oklch(0.52 0.09 128)" }}>
              {t("funnel.path2Badge")}
            </div>
            <h3 className="text-2xl font-bold text-[oklch(0.2_0.02_80)] mb-2">{t("funnel.path2Title")}</h3>
            <p className="text-[oklch(0.78_0.11_85)] font-medium mb-6" style={{ fontFamily: "var(--font-body)" }}>{t("funnel.path2Sub")}</p>
            <p className="text-[oklch(0.45_0.02_80)] mb-6 leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.path2Desc")}
            </p>
            <ul className="space-y-3 mb-8">
              {[
                t("funnel.path2Feature1"),
                t("funnel.path2Feature2"),
                t("funnel.path2Feature3"),
                t("funnel.path2Feature4"),
                t("funnel.path2Feature5"),
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check size={16} className="text-[oklch(0.52_0.09_128)] shrink-0" />
                  <span className="text-sm text-[oklch(0.35_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>{item}</span>
                </li>
              ))}
            </ul>
            <a href="#start" className="inline-flex items-center gap-2 text-[oklch(0.52_0.09_128)] font-semibold hover:gap-3 transition-all" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.path2Cta")} <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 7. Fundament – Trust/Ethik ─── */
function TrustSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();
  const badges = [
    { icon: Leaf, title: t("funnel.trust1Title"), sub: t("funnel.trust1Sub") },
    { icon: Heart, title: t("funnel.trust2Title"), sub: t("funnel.trust2Sub") },
    { icon: Shield, title: t("funnel.trust3Title"), sub: t("funnel.trust3Sub") },
    { icon: Moon, title: t("funnel.trust4Title"), sub: t("funnel.trust4Sub") },
    { icon: Sun, title: t("funnel.trust5Title"), sub: t("funnel.trust5Sub") },
  ];

  return (
    <section className="py-24 lg:py-32" ref={ref}>
      <div className="container">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.trustBadge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] mt-4">
            {t("funnel.trustTitle")}
          </h2>
          <p className="text-lg text-[oklch(0.45_0.02_80)] mt-4 max-w-2xl mx-auto" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.trustDesc")}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-6">
          {badges.map((b, i) => (
            <div
              key={i}
              className={`flex items-center gap-4 px-6 py-4 rounded-2xl bg-[oklch(0.97_0.005_80)] border border-[oklch(0.9_0.01_80)] transition-all duration-500 hover:shadow-md hover:border-[oklch(0.52_0.09_128/0.2)] ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <b.icon size={20} className="text-[oklch(0.52_0.09_128)]" />
              <div>
                <div className="text-sm font-bold text-[oklch(0.2_0.02_80)]">{b.title}</div>
                <div className="text-xs text-[oklch(0.5_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>{b.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 8. Testimonials ─── */
function FunnelTestimonials() {
  const { ref, visible } = useInView();
  const { t } = useI18n();

  return (
    <section className="py-24 lg:py-32 bg-[oklch(0.97_0.005_80)]" ref={ref}>
      <div className="container">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.testimonialsBadge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] mt-4">
            {t("funnel.testimonialsTitle")}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((testimonial, i) => (
            <div
              key={i}
              className={`p-6 rounded-2xl bg-white border border-[oklch(0.9_0.01_80)] transition-all duration-500 hover:shadow-lg ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <p className="text-[oklch(0.35_0.02_80)] leading-relaxed mb-4 italic" style={{ fontFamily: "var(--font-body)" }}>
                "{t(testimonial.textKey as any)}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: "oklch(0.52 0.09 128)" }}>
                  {testimonial.name.charAt(0)}
                </div>
                <span className="font-semibold text-sm text-[oklch(0.3_0.02_80)]">{testimonial.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 9. Gründer-Story (Annam) ─── */
function FounderSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" style={{ background: "oklch(0.18 0.02 280)" }} ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <img src={IMAGES.mandala} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl" style={{ background: "linear-gradient(135deg, oklch(0.78 0.11 85 / 0.15), oklch(0.52 0.09 128 / 0.1))", filter: "blur(30px)" }} />
              <img
                src={IMAGES.annamOrange}
                alt="Annam Hanfstein"
                className="relative rounded-3xl w-full object-cover shadow-2xl"
                style={{ maxHeight: "500px" }}
              />
            </div>
            <div className="flex flex-wrap gap-3 mt-6 justify-center">
              {[t("funnel.founderTag1"), t("funnel.founderTag2"), t("funnel.founderTag3")].map((tag, i) => (
                <span key={i} className="px-4 py-1.5 rounded-full text-xs font-medium bg-[oklch(0.78_0.11_85/0.1)] text-[oklch(0.78_0.11_85)] border border-[oklch(0.78_0.11_85/0.2)]">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.78_0.11_85)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.founderBadge")}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[oklch(0.95_0.01_80)] mt-4 mb-6">
              {t("funnel.founderTitle1")}{" "}
              <br className="hidden sm:block" />
              {t("funnel.founderTitle2")}
            </h2>
            <div className="space-y-4 text-[oklch(0.75_0.01_80)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              <p>{t("funnel.founderText1")}</p>
              <p>{t("funnel.founderText2")}</p>
              <p>{t("funnel.founderText3")}</p>
            </div>

            <div className="mt-8 p-6 rounded-2xl border border-[oklch(0.78_0.11_85/0.2)] bg-[oklch(0.78_0.11_85/0.05)]">
              <p className="text-[oklch(0.85_0.01_80)] italic leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
                "{t("funnel.founderQuote")}"
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img src={IMAGES.annamBlue} alt="Annam" className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-sm font-bold text-[oklch(0.95_0.01_80)]">{t("funnel.founderName")}</div>
                  <div className="text-xs text-[oklch(0.6_0.01_80)]">{t("funnel.founderRole")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── 10. CTA – Bereit? ─── */
function ReadySection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img src={IMAGES.herbs} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, oklch(0.97 0.005 80 / 0.92), oklch(0.95 0.01 80 / 0.88))" }} />
      </div>
      <div className="container relative z-10 text-center">
        <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] mb-6 transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}>
          {t("funnel.readyTitle1")}{" "}
          <br className="hidden sm:block" />
          {t("funnel.readyTitle2")}
        </h2>
        <p className={`text-lg text-[oklch(0.45_0.02_80)] mb-10 max-w-xl mx-auto transition-all duration-700 delay-200 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`} style={{ fontFamily: "var(--font-body)" }}>
          {t("funnel.readyDesc")}
        </p>
        <div className={`flex flex-wrap justify-center gap-4 mb-8 transition-all duration-700 delay-300 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}>
          {[t("funnel.readyTag1"), t("funnel.readyTag2"), t("funnel.readyTag3")].map((tag, i) => (
            <span key={i} className="px-5 py-2 rounded-full text-sm font-medium bg-white/80 text-[oklch(0.35_0.02_80)] border border-[oklch(0.9_0.01_80)]" style={{ fontFamily: "var(--font-body)" }}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 11. CTA / Nächster Schritt ─── */
function NextStepSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();

  return (
    <section id="start" className="py-24 lg:py-32" style={{ background: "oklch(0.18 0.02 280)" }} ref={ref}>
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.95_0.01_80)]">
            {t("funnel.nextTitle")}
          </h2>
          <p className="text-lg text-[oklch(0.65_0.01_80)] mt-4 max-w-xl mx-auto" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.nextDesc")}
          </p>
        </div>

        <div className="max-w-xl mx-auto mb-12">
          <a
            href="/registrierung"
            className={`group block p-10 rounded-3xl border border-[oklch(0.52_0.09_128/0.3)] bg-[oklch(0.52_0.09_128/0.05)] transition-all duration-500 hover:border-[oklch(0.52_0.09_128/0.6)] hover:shadow-xl text-center ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="w-16 h-16 rounded-2xl bg-[oklch(0.52_0.09_128/0.15)] flex items-center justify-center mx-auto mb-6">
              <ArrowRight size={28} className="text-[oklch(0.52_0.09_128)]" />
            </div>
            <h3 className="text-2xl font-bold text-[oklch(0.95_0.01_80)] mb-3">{t("funnel.nextCardTitle")}</h3>
            <p className="text-[oklch(0.65_0.01_80)] leading-relaxed max-w-md mx-auto" style={{ fontFamily: "var(--font-body)" }}>
              {t("funnel.nextCardDesc")}
            </p>
          </a>
        </div>

        {/* Contact */}
        <div className={`text-center transition-all duration-700 delay-300 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}>
          <a
            href="mailto:office@ayu-rveda.ai"
            className="inline-flex items-center gap-2 text-[oklch(0.65_0.01_80)] hover:text-[oklch(0.78_0.11_85)] transition-colors"
            style={{ fontFamily: "var(--font-body)" }}
          >
            <Mail size={16} />
            {t("funnel.nextContact")}
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── Chat-Vorschau Sektion (analog MENTO) ─── */
function ChatPreviewSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  const chatMessages = [
    { type: "date" as const, text: t("funnel.chatMsg1Date") },
    { type: "user" as const, text: t("funnel.chatMsg1User") },
    { type: "bot" as const, label: "AYU", text: t("funnel.chatMsg1Bot") },
    { type: "user" as const, text: t("funnel.chatMsg2User") },
    { type: "bot" as const, text: t("funnel.chatMsg2Bot") },
    { type: "user" as const, text: t("funnel.chatMsg3User") },
    { type: "bot" as const, label: "EMPFEHLUNG", text: t("funnel.chatMsg3Bot") },
  ];

  const scrollToBottom = useCallback(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
    }
  }, []);

  const runAnimation = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setVisibleCount(0);
    setShowTyping(false);
    let idx = 0;
    const showNext = () => {
      if (idx >= chatMessages.length) {
        setShowTyping(true);
        setTimeout(scrollToBottom, 100);
        setTimeout(() => {
          setIsAnimating(false);
          setVisibleCount(0);
          setShowTyping(false);
          setTimeout(() => runAnimation(), 2000);
        }, 6000);
        return;
      }
      const msg = chatMessages[idx];
      if (msg.type === "bot") {
        setShowTyping(true);
        setTimeout(scrollToBottom, 50);
        const dur = Math.min(900 + (msg.text?.length || 0) * 10, 2200);
        setTimeout(() => {
          setShowTyping(false);
          idx++;
          setVisibleCount(idx);
          setTimeout(scrollToBottom, 50);
          setTimeout(showNext, 400);
        }, dur);
      } else if (msg.type === "date") {
        idx++;
        setVisibleCount(idx);
        setTimeout(scrollToBottom, 50);
        setTimeout(showNext, 600);
      } else {
        idx++;
        setVisibleCount(idx);
        setTimeout(scrollToBottom, 50);
        setTimeout(showNext, 800);
      }
    };
    setTimeout(showNext, 800);
  }, [isAnimating, scrollToBottom]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStarted.current) {
            hasStarted.current = true;
            setTimeout(() => runAnimation(), 500);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [runAnimation]);

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" style={{ background: "oklch(0.97 0.005 80)" }} ref={ref}>
      <div className="container relative z-10">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.chatBadge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] mt-4">
            {t("funnel.chatTitle1")} <span className="text-[oklch(0.52_0.09_128)]">{t("funnel.chatTitle2")}</span>
          </h2>
          <p className="text-lg text-[oklch(0.45_0.02_80)] mt-4 max-w-2xl mx-auto" style={{ fontFamily: "var(--font-body)" }}>
            {t("funnel.chatDesc")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Phone Mockup */}
          <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="relative flex justify-center">
              <div className="absolute inset-0 bg-[oklch(0.52_0.09_128/0.05)] rounded-full blur-[80px]" />
              <div className="relative mx-auto" style={{ width: "300px" }}>
                <div className="relative rounded-[40px] overflow-hidden shadow-2xl" style={{ background: "linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 50%, #111 100%)", padding: "12px", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <div className="relative rounded-[30px] overflow-hidden" style={{ background: "#0f1a0f", height: "580px" }}>
                    {/* Status bar */}
                    <div className="flex items-center justify-between px-6 pt-3 pb-1 relative">
                      <span className="text-[11px] font-semibold text-white/80">9:41</span>
                      <div className="w-[90px] h-[28px] bg-black rounded-full" style={{ position: "absolute", top: "10px", left: "50%", transform: "translateX(-50%)" }} />
                      <div className="flex items-center gap-1">
                        <svg width="14" height="10" viewBox="0 0 14 10" fill="white" opacity="0.8"><rect x="0" y="6" width="2.5" height="4" rx="0.5" /><rect x="3.5" y="4" width="2.5" height="6" rx="0.5" /><rect x="7" y="2" width="2.5" height="8" rx="0.5" /><rect x="10.5" y="0" width="2.5" height="10" rx="0.5" /></svg>
                        <svg width="14" height="10" viewBox="0 0 24 16" fill="white" opacity="0.8"><path d="M1 5.5C4.5 1.5 9 0 12 0s7.5 1.5 11 5.5l-2 2C18 4 15 2.5 12 2.5S6 4 3 7.5l-2-2z" /><path d="M5 9.5C7 7 9.5 6 12 6s5 1 7 3.5l-2 2C15.5 9.5 14 9 12 9s-3.5.5-5 2.5l-2-2z" /><circle cx="12" cy="14" r="2" /></svg>
                        <svg width="22" height="10" viewBox="0 0 25 11" fill="none" stroke="white" opacity="0.8"><rect x="0.5" y="0.5" width="20" height="10" rx="2" strokeWidth="1" /><rect x="22" y="3" width="2" height="5" rx="1" fill="white" opacity="0.4" /><rect x="1.5" y="1.5" width="14" height="8" rx="1" fill="white" opacity="0.8" /></svg>
                      </div>
                    </div>
                    {/* Chat Header */}
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0" style={{ background: "linear-gradient(135deg, oklch(0.52 0.09 128), oklch(0.42 0.09 128))" }}>
                        <div className="w-full h-full flex items-center justify-center">
                          <img src={IMAGES.logo} alt="AYU" className="w-5 h-5 object-contain" />
                        </div>
                      </div>
                      <div>
                        <div className="text-white text-[13px] font-semibold leading-tight">AYU</div>
                        <div className="text-[oklch(0.52_0.09_128)] text-[10px] leading-tight">{t("funnel.chatCompanion")}</div>
                      </div>
                      <div className="ml-auto w-2 h-2 rounded-full bg-[oklch(0.52_0.09_128)] animate-pulse" />
                    </div>
                    {/* Chat Messages */}
                    <div ref={chatRef} className="px-3 py-4 space-y-3 overflow-y-auto" style={{ height: "calc(100% - 130px)", scrollbarWidth: "none" }}>
                      {chatMessages.map((msg, i) => (
                        <div key={i} className="transition-all duration-500" style={{ opacity: i < visibleCount ? 1 : 0, transform: i < visibleCount ? "translateY(0)" : "translateY(12px)" }}>
                          {msg.type === "date" ? (
                            <div className="text-center"><span className="text-[10px] text-white/30 px-3 py-1 rounded-full bg-white/5 font-medium">{msg.text}</span></div>
                          ) : msg.type === "user" ? (
                            <div className="flex justify-end"><div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-[11.5px] leading-[1.5]" style={{ background: "oklch(0.52 0.09 128)", color: "white" }}>{msg.text}</div></div>
                          ) : (
                            <div className="flex justify-start"><div className="max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-bl-md text-[11.5px] leading-[1.55]" style={{ background: "rgba(255,255,255,0.08)", color: "oklch(0.88 0.01 80)", border: "1px solid rgba(255,255,255,0.06)" }}>
                              {msg.label && <div className="flex items-center gap-1.5 mb-1.5"><span className="text-[oklch(0.52_0.09_128)] text-[9px]">✦</span><span className="text-[oklch(0.78_0.11_85)] text-[9px] font-semibold tracking-wider uppercase">{msg.label}</span></div>}
                              <span>{msg.text}</span>
                            </div></div>
                          )}
                        </div>
                      ))}
                      {showTyping && (
                        <div className="flex justify-start"><div className="px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.04)" }}>
                          <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "0ms", animationDuration: "0.8s" }} />
                          <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "150ms", animationDuration: "0.8s" }} />
                          <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "300ms", animationDuration: "0.8s" }} />
                        </div></div>
                      )}
                      <div className="h-12" />
                    </div>
                    {/* Input bar */}
                    <div className="absolute bottom-0 left-0 right-0 px-3 pb-6 pt-3" style={{ background: "linear-gradient(to top, #0f1a0f 70%, transparent)" }}>
                      <div className="flex items-center gap-2 px-4 py-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
                        <span className="text-white/30 text-[12px] flex-1">{t("funnel.chatInputPlaceholder")}</span>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "oklch(0.52 0.09 128)" }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" /></svg>
                        </div>
                      </div>
                      <div className="w-[120px] h-[4px] bg-white/20 rounded-full mx-auto mt-3" />
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[80%] h-8 bg-[oklch(0.52_0.09_128/0.1)] blur-xl rounded-full" />
              </div>
            </div>
          </div>

          {/* Right: Description */}
          <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <h3 className="text-2xl sm:text-3xl font-bold text-[oklch(0.2_0.02_80)] mb-6">
              {t("funnel.chatSubtitle")}
            </h3>
            <div className="space-y-6">
              {[
                { title: t("funnel.chatFeature1Title"), text: t("funnel.chatFeature1Text") },
                { title: t("funnel.chatFeature2Title"), text: t("funnel.chatFeature2Text") },
                { title: t("funnel.chatFeature3Title"), text: t("funnel.chatFeature3Text") },
                { title: t("funnel.chatFeature4Title"), text: t("funnel.chatFeature4Text") },
              ].map((item, i) => (
                <div key={i} className={`flex items-start gap-4 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{ transitionDelay: `${i * 150 + 400}ms` }}>
                  <div className="w-10 h-10 rounded-lg bg-[oklch(0.52_0.09_128/0.1)] flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={18} className="text-[oklch(0.52_0.09_128)]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[oklch(0.2_0.02_80)] mb-1">{item.title}</h4>
                    <p className="text-[oklch(0.45_0.02_80)] leading-relaxed text-[15px]" style={{ fontFamily: "var(--font-body)" }}>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function FunnelFooter() {
  const { t } = useI18n();
  return (
    <footer className="py-8 border-t border-[oklch(0.9_0.01_80)]" style={{ background: "oklch(0.97 0.005 80)" }}>
      <div className="container flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="AYU" className="h-8 w-auto" />
          <span className="text-xs text-[oklch(0.5_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>
            © {new Date().getFullYear()} AYU Healthtech LIMITED
          </span>
        </div>
        <div className="flex items-center gap-6">
          <a href="/" className="text-xs text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors" style={{ fontFamily: "var(--font-body)" }}>{t("funnel.footerStartseite")}</a>
          <a href="/impressum" className="text-xs text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors" style={{ fontFamily: "var(--font-body)" }}>{t("funnel.footerImpressum")}</a>
          <a href="/datenschutz" className="text-xs text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors" style={{ fontFamily: "var(--font-body)" }}>{t("funnel.footerDatenschutz")}</a>
        </div>
      </div>
    </footer>
  );
}

/* ─── Main Funnel Page ─── */
export default function Funnel() {
  const { t } = useI18n();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Meta Pixel: ViewContent-Event bei Funnel-Seite
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', {
        content_name: 'AYU Funnel',
        content_category: 'Funnel',
      });
    }
  }, []);

  return (
    <div className="min-h-screen">
      <FunnelNav />
      <FunnelHero />
      <PainPointsSection />
      <InlineCta text={t("funnel.inlineCta1Text")} buttonText={t("funnel.inlineCta1Button")} variant="accent" />
      <FeelSection />
      <WhatIsAyuSection />
      <InlineCta text={t("funnel.inlineCta2Text")} buttonText={t("funnel.inlineCta2Button")} />
      <DnaSection />
      <TwoPathsSection />
      <InlineCta text={t("funnel.inlineCta3Text")} buttonText={t("funnel.inlineCta3Button")} variant="dark" />
      <TrustSection />
      <FunnelTestimonials />
      <InlineCta text={t("funnel.inlineCta4Text")} buttonText={t("funnel.inlineCta4Button")} variant="accent" />
      <FounderSection />
      <ChatPreviewSection />
      <ReadySection />
      <NextStepSection />
      <FunnelFooter />
    </div>
  );
}
